import React, { useEffect, useState, useContext } from 'react';
import axiosConn from '../../axiosConn';
import './galaxyRightbar.css';
import { AuthContext } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import useStyles from '../../utils/useStyles';
import {
  isPlayerAuthorized,
} from '../../utils/galaxyUtils';
import { usePresence } from '../../context/PresenceContext';
import useGalaxySubscriberPresence from '../../hooks/useGalaxySubscriberPresence';
import useGalaxyMemberAdministration from '../../hooks/useGalaxyMemberAdministration';
import GalaxyMemberRestoration from '../galaxyRightbar/GalaxyMemberRestoration';

const AVAILABILITY_LABELS = {
  online: 'Online',
  idle: 'Idle',
  recent: 'Recently online',
};

function GalaxyRightBar({
  galaxy,
  onJgkTokenSumChange,
  galaxyChatOnlinePlayers,
  hydratedMembers,
  onMembersChanged,
}) {
  const hasHydratedMembers = Array.isArray(hydratedMembers);
  const [subscribers, setSubscribers] = useState(
    hasHydratedMembers ? hydratedMembers : [],
  );
  const { player } = useContext(AuthContext);
  const { presenceMap } = usePresence() || {};
  const subscriberPresence = useGalaxySubscriberPresence({
    subscribers,
    presenceMap,
    galaxyChatOnlinePlayers,
  });
  const classes = useStyles();
  const administration = useGalaxyMemberAdministration({ galaxy, player, subscribers, setSubscribers, onMembersChanged });

  // Pass the sum to the parent component
  // useEffect(() => {
  //   onJgkTokenSumChange(jgkTokenSum);
  // }, [jgkTokenSum, onJgkTokenSumChange]);
  // const isPlayerAuthorized =
  //   (galaxy.owners || []).some(
  //     (owner) => owner._id === player.data.player._id,
  //   ) ||
  //   (galaxy.administrators || []).some(
  //     (admin) => admin._id === player.data.player._id,
  //   ) ||
  //   (galaxy.moderators || []).some(
  //     (moderator) => moderator._id === player.data.player._id,
  //   );

  // const handleRemoveSubscriber = async (subscriberId) => {
  //   if (isPlayerAuthorized) {
  //     try {
  //       await axiosConn.delete(
  //         `/galaxies/${galaxy._id}/remove/${subscriberId}`,
  //         {
  //           headers: {
  //             Authorization: `Bearer ${player.token}`,
  //           },
  //         },
  //       );
  //       setSubscribers(
  //         subscribers.filter((subscriber) => subscriber._id !== subscriberId),
  //       );
  //     } catch (error) {
  //       console.error('Error removing subscriber:', error);
  //     }
  //   }
  // };

  useEffect(() => {
    if (!hasHydratedMembers) return;
    setSubscribers(hydratedMembers);
  }, [hasHydratedMembers, hydratedMembers]);

  useEffect(() => {
    if (!hasHydratedMembers) return;
    const jgkTokenSum = subscribers.reduce(
      (sum, member) => sum + (Number(member.jgkTokens) || 0),
      0,
    );
    onJgkTokenSumChange(jgkTokenSum);
  }, [hasHydratedMembers, subscribers, onJgkTokenSumChange]);

  useEffect(() => {
    if (hasHydratedMembers) return undefined;
    let mounted = true;
    const fetchSubscribers = async () => {
      try {
        const response = await axiosConn.get(
          `galaxies/${galaxy._id}/members`,
          {
            headers: {
              Authorization: `Bearer ${player.token}`,
            },
          },
        );
        if (!mounted) return;
        setSubscribers(response.data.members);

        // Calculate the sum of jgkToken after the subscribers are fetched
        const jgkTokenSum = response.data.members.reduce(
          (sum, member) => sum + (Number(member.jgkTokens) || 0),
          0,
        );
        onJgkTokenSumChange(jgkTokenSum);
      } catch (error) {
        console.error('Failed to fetch subscribers: ', error);
      }
    };

    fetchSubscribers();
    return () => {
      mounted = false;
    };
  }, [galaxy, player.token, onJgkTokenSumChange, hasHydratedMembers]);

  // console.log(window.location.pathname);
  // console.log(galaxyChatOnlinePlayers, subscribers);
  return (
    <div className="galaxyRightBar">
      <h4 className="rightBarTitle">
        Galaxy Subscribers:{' '}
        {subscribers &&
          subscribers.some(
            (subscriber) => subscriber._id === player.data.player._id,
          ) &&
          subscribers.length}
      </h4>{' '}
      <GalaxyMemberRestoration administration={administration} />
      {subscribers &&
        (administration.authorized || subscribers.some(
          (subscriber) => subscriber._id === player.data.player._id,
        )) && (
          <div className="rightbarFollowings">
            {[...subscribers]
              .sort((a, b) => b.jgkTokens - a.jgkTokens)
              .map((subscriber) => {
                const subscriberKey =
                  subscriber._id ||
                  subscriber.playerId ||
                  subscriber.accountId ||
                  subscriber.id;
                const presenceState = subscriberPresence[subscriberKey] || {};
                const availabilityStatus =
                  presenceState.availabilityStatus || 'none';
                const availabilityLabel =
                  AVAILABILITY_LABELS[availabilityStatus];

                return (
                  <div key={subscriberKey} className="subscriberCard">
                    <Link to={`/profile/${subscriber.playerId}`}>
                      <div className="subscriberPhotoWrapper">
                        <img
                          className="subscriberPhoto"
                          src={
                            subscriber.profilePhoto
                              ? subscriber.profilePhoto
                              : 'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png'
                          }
                          alt={subscriber.playerId}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src =
                              'https://res.cloudinary.com/joegalaxy/image/upload/q_auto/v1656194537/Images/noAvatar_tsgpm9.png';
                          }}
                        />
                        {presenceState.isPresenceVisible && (
                          <span
                            aria-label={availabilityLabel}
                            className={`galaxySubscriberPresenceDot galaxySubscriberPresenceDot--${availabilityStatus}`}
                            role="img"
                            title={availabilityLabel}
                          />
                        )}
                        {presenceState.isInGalaxy && (
                          <span
                            aria-label="In this Galaxy"
                            className="galaxySubscriberPresenceDot galaxySubscriberPresenceDot--galaxy"
                            role="img"
                            title="In this Galaxy"
                          />
                        )}
                      </div>
                    </Link>
                    <div className="subscriberInfo">
                      {
                        <>
                          {isPlayerAuthorized(galaxy, player) ? (
                            <button
                              variant="contained"
                              className={classes.buttonSubscribed}
                              onClick={() => administration.remove(subscriber)}
                              disabled={administration.busy}
                              title={`Remove ${subscriber.playerId}`}
                            >
                              {subscriber.playerId}
                            </button>
                          ) : (
                            <span className="subscriberId">
                              {subscriber.playerId}
                            </span>
                          )}
                        </>
                      }
                      <span className="subscriberTokens highlighted">
                        {Number(subscriber.jgkTokens)
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
    </div>
  );
}

export default GalaxyRightBar;
